import { getCustomers } from 'Hooks/Api/apiCustomer';
import type { LiquorLicenceDetails } from 'Hooks/Api/apiLiquorLicense';
import { validateLiquorLicenceNumber } from 'Hooks/Api/apiLiquorLicense';
import { validateAbn, validateAcn } from 'Hooks/useAusBusinessNumber';
import { action, observable, runInAction } from 'mobx';
import * as Models from 'Models/Entities';
import { customerCreationSourceOptions } from 'Models/Enums/MappedEnumOptions';
import {
	attribute,
	entity,
	IAttributeGroup,
	IModelAttributes,
	Model,
	ReferencePath,
} from 'Models/Model';
import UserCustomerEntity from 'Models/Security/Acl/UserCustomerEntity';
import VisitorsCustomerEntity from 'Models/Security/Acl/VisitorsCustomerEntity';
import { IAcl } from 'Models/Security/IAcl';
import moment from 'moment';
import * as AttrUtils from 'Util/AttributeUtils';
import {
	getCreatedModifiedCrudOptions,
	makeEnumFetchFunction,
	makeFetchManyToManyFunc,
	makeFetchOneToManyFunc,
	makeJoinEqualsFunc,
} from 'Util/EntityUtils';
import { LocationUtil } from 'Util/LocationUtil';
import * as Validators from 'Validators';
import { isPhone } from 'Validators/Functions/Custom';
import { isEmail } from 'Validators/Functions/Email';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { CRUD } from '../CRUDOptions';
import type { CustomerCreationSource } from '../Enums';
import * as Enums from '../Enums';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

export interface ICustomerEntityAttributes extends IModelAttributes {
	drinksLicence: string;
	email: string;
	tradingTerms: string;
	purchasingOfficerEmail: string;
	taxInvoicesEmail: string;
	statementsEmail: string;
	acn: string;
	accountsPhoneNumber: string;
	creditLimit: string;
	abn: string;
	businessName: string;
	trusteeACN: string;
	debtorID: string;
	dateOfBirth: Date;
	firstName: string;
	nameForInvoice: string;
	phone: string;
	taxInvoicesContact: string;

	purchasingOfficer: string;
	statementsContact: string;
	sicCode: string;

	drinksLicenceInfo: string;
	drinksLicenceState: Enums.state;
	drinksLicenceStatus: string;
	drinksLicenceLastUpdated: Date;

	paymentRiskRating: Enums.riskScore;
	paymentRiskRatingLastUpdated?: Date;

	lastName: string;
	monitoredStatus: Enums.customerMonitoredStatus;

	accountsContact: string;

	atbRecords: Array<
		| Models.AtbRecordEntity
		| Models.IAtbRecordEntityAttributes
	>;
	customerAuditss: Array<
		| Models.CustomerAuditEntity
		| Models.ICustomerAuditEntityAttributes
	>;
	businessEntityId: string;
	businessEntity: Models.BusinessEntity | Models.IBusinessEntityAttributes;
	groupss: Array<
		| Models.GroupsCustomers
		| Models.IGroupsCustomersAttributes
	>;
	monitoredss: Array<
		| Models.MonitoredsCustomers
		| Models.IMonitoredsCustomersAttributes
	>;
	organisationId?: string;
	organisation?: Models.OrganisationEntity | Models.IOrganisationEntityAttributes;
	physicalAddress?: Models.AddressEntity |
	Models.IAddressEntityAttributes;
	postalAddress?: Models.AddressEntity |
	Models.IAddressEntityAttributes;
	reportRequests: Array<
		| Models.ReportRequestEntity
		| Models.IReportRequestEntityAttributes
	>;
	currentAtbRecord: Models.AtbRecordEntity;
	newestAlares: Models.AlaresReportEntity;
	businessEntityStatus: boolean | null;
	liquorLicenceFinishedValidating: boolean;
	// eslint-disable-next-line max-len
	liquorLicenceResponseDetails?: { data: Record<string, LiquorLicenceDetails>; responseCode: number; responseMessage: string };
	selectedLiquorLicenceDetails?: LiquorLicenceDetails;
	monitoringPaygEnd: Date | null;
	creationSource: CustomerCreationSource;
}

// % protected region % [Customise your entity metadata here] on begin

const AttributeGroupMonitoring: number = 1;
const AttributeGroupOther: number = 99;


@entity('CustomerEntity', 'Customer')
export default class CustomerEntity extends Model
	implements ICustomerEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsCustomerEntity(),
		new UserCustomerEntity(),
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] on begin
		'currentAtbRecord',
		'newestAlares',
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] on begin
		'currentAtbRecord',
		'newestAlares',
		// % protected region % [Add any custom update exclusions here] end
	];

	// % protected region % [Modify props to the crud options here for attribute 'Drinks Licence'] on begin
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Drinks Licence',
		displayType: 'textfield',
		order: 10,
		headerColumn: false,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public drinksLicence: string;

	// % protected region % [Modify props to the crud options here for attribute 'Email'] on begin
	/**
	 * Email
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Email',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public email: string;

	// % protected region % [Modify props to the crud options here for attribute 'Trading terms'] on begin
	/**
	 * Trading terms
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Trading terms',
		displayType: 'textfield',
		order: 100,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public tradingTerms: string;
	

	// % protected region % [Modify props to the crud options here for attribute 'Purchasing Officer Email'] on begin
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Purchasing Officer Email',
		displayType: 'textfield',
		order: 110,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public purchasingOfficerEmail: string;

	// % protected region % [Modify props to the crud options here for attribute 'Tax Invoices Email'] on begin
	/**
	 * Tax Invoices Email
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Tax Invoices Email',
		displayType: 'textfield',
		order: 130,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public taxInvoicesEmail: string;
	

	// % protected region % [Modify props to the crud options here for attribute 'Trustee ACN'] on begin
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Trustee ACN',
		displayType: 'textfield',
		order: 140,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public trusteeACN: string;
	

	
	/**
	 * Statements Email
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Statements Email',
		displayType: 'textfield',
		order: 150,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public statementsEmail: string;
	

	// % protected region % [Modify props to the crud options here for attribute 'ACN'] on begin
	/**
	 * ACN
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'ACN',
		displayType: 'textfield',
		order: 160,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public acn: string;


	// % protected region % [Modify props to the crud options here for attribute 'Accounts Phone Number'] on begin
	/**
	 * Accounts Phone Number
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Accounts Phone Number',
		displayType: 'textfield',
		order: 190,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public accountsPhoneNumber: string;
	

	// % protected region % [Modify props to the crud options here for attribute 'Credit Limit'] on begin
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Credit Limit',
		displayType: 'textfield',
		order: 200,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public creditLimit: string;


	// % protected region % [Modify props to the crud options here for attribute 'ABN'] on begin
	/**
	 * ABN
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'ABN',
		displayType: 'textfield',
		order: 230,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public abn: string;

	// % protected region % [Modify props to the crud options here for attribute 'Business Name'] on begin
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Business Name',
		displayType: 'textfield',
		order: 250,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public businessName: string;
	

	// % protected region % [Modify props to the crud options here for attribute 'Debtor ID'] on begin
	/**
	 * A customer's debtor ID represents the unique identifier used by business entities to represent their customers
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Debtor ID',
		displayType: 'textfield',
		order: 280,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public debtorID: string;

	// % protected region % [Modify props to the crud options here for attribute 'Date of Birth'] on begin
	/**
	 * Date of Birth
	 */
	@observable
	@attribute<CustomerEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	@CRUD({
		name: 'Date of Birth',
		displayType: 'datepicker',
		order: 310,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: attr => AttrUtils.standardiseDate(attr, { serialiseAs: 'date' }),
		groupId: AttributeGroupOther,
	})
	public dateOfBirth: Date;


	// % protected region % [Modify props to the crud options here for attribute 'First Name'] on begin
	/**
	 * First Name
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'First Name',
		displayType: 'textfield',
		order: 340,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,

	})
	public firstName: string;

	// % protected region % [Modify props to the crud options here for attribute 'Name for Invoice'] on begin
	/**
	 * Name for Invoice
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Name for Invoice',
		displayType: 'textfield',
		order: 360,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,

	})
	public nameForInvoice: string;
	

	// % protected region % [Modify props to the crud options here for attribute 'Phone'] on begin
	/**
	 * Phone
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Phone',
		displayType: 'textfield',
		order: 390,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,

	})
	public phone: string;
	

	// % protected region % [Modify props to the crud options here for attribute 'Tax Invoices Contact'] on begin
	/**
	 * Tax Invoices Contact
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Tax Invoices Contact',
		displayType: 'textfield',
		order: 400,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public taxInvoicesContact: string;
	

	// % protected region % [Modify props to the crud options here for attribute 'Drinks Licence Last Updated'] on begin
	@observable
	@attribute<CustomerEntity, Date>()
	@CRUD({
		name: 'Drinks Licence Last Updated',
		displayType: 'datetimepicker',
		order: 410,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseDate,
		groupId: AttributeGroupOther,
	})
	public drinksLicenceLastUpdated: Date;
	

	// % protected region % [Modify props to the crud options here for attribute 'Payment Risk Rating'] on begin
	@observable
	@attribute<CustomerEntity, Enums.riskScore>()
	@CRUD({
		name: 'Payment Risk Rating',
		displayType: 'enum-combobox',
		order: 420,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.riskScoreOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.riskScoreOptions),
		displayFunction: (attr: Enums.riskScore) => Enums.riskScoreOptions[attr],
		groupId: AttributeGroupOther,
	})
	public paymentRiskRating: Enums.riskScore;

	@observable
	@attribute<CustomerEntity, Date>()
	@CRUD({
		name: 'Payment Risk Last Updated',
		displayType: 'datetimepicker',
		order: 430,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseDate,
		groupId: AttributeGroupOther,
	})
	public paymentRiskRatingLastUpdated?: Date;

	// % protected region % [Modify props to the crud options here for attribute 'Purchasing Officer'] on begin
	/**
	 * Purchasing Officer
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Purchasing Officer',
		displayType: 'textfield',
		order: 440,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public purchasingOfficer: string;
	

	// % protected region % [Modify props to the crud options here for attribute 'Statements Contact'] on begin
	/**
	 * Statements Contact
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Statements Contact',
		displayType: 'textfield',
		order: 450,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public statementsContact: string;
	

	// % protected region % [Modify props to the crud options here for attribute 'Sic Code'] on begin
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Sic Code',
		displayType: 'textfield',
		order: 460,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public sicCode: string;
	

	// % protected region % [Modify props to the crud options here for attribute 'Drinks Licence Info'] on begin
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Drinks Licence Info',
		displayType: 'textfield',
		order: 470,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public drinksLicenceInfo: string;


	// % protected region % [Modify props to the crud options here for attribute 'Drinks Licence State'] on begin
	@observable
	@attribute<CustomerEntity, Enums.state>()
	@CRUD({
		name: 'Drinks Licence State',
		displayType: 'enum-combobox',
		order: 500,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.stateOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.stateOptions),
		displayFunction: (attr: Enums.state) => Enums.stateOptions[attr],
		groupId: AttributeGroupOther,
	})
	public drinksLicenceState: Enums.state;
	

	// % protected region % [Modify props to the crud options here for attribute 'Last Name'] on begin
	/**
	 * Last Name
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Last Name',
		displayType: 'textfield',
		order: 510,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public lastName: string;
	

	// % protected region % [Modify props to the crud options here for attribute 'Monitored Status'] on begin
	/**
	 * If the customer is being actively monitored, in a dormant state, or insolvent
	 */
	@observable
	@attribute<CustomerEntity, Enums.customerMonitoredStatus>()
	@CRUD({
		name: 'Monitored Status',
		displayType: 'enum-combobox',
		order: 530,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.customerMonitoredStatusOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.customerMonitoredStatusOptions),
		displayFunction: (attr: Enums.customerMonitoredStatus) => Enums.customerMonitoredStatusOptions[attr],
		groupId: AttributeGroupMonitoring,
	})
	public monitoredStatus: Enums.customerMonitoredStatus;
	

	// % protected region % [Modify props to the crud options here for attribute 'Drinks Licence Status'] on begin
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Drinks Licence Status',
		displayType: 'textfield',
		order: 540,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public drinksLicenceStatus: string;
	

	// % protected region % [Modify props to the crud options here for attribute 'Accounts Contact'] on begin
	/**
	 * Accounts Contact
	 */
	@observable
	@attribute<CustomerEntity, string>()
	@CRUD({
		name: 'Accounts Contact',
		displayType: 'textfield',
		order: 550,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
		groupId: AttributeGroupOther,
	})
	public accountsContact: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'ATB Records',
		displayType: 'reference-multicombobox',
		order: 560,
		referenceTypeFunc: () => Models.AtbRecordEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'atbRecords',
			oppositeEntity: () => Models.AtbRecordEntity,
		}),
		groupId: AttributeGroupOther,
		
	})
	public atbRecords: Models.AtbRecordEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Customer Auditss',
		displayType: 'reference-multicombobox',
		order: 580,
		referenceTypeFunc: () => Models.CustomerAuditEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'customerAuditss',
			oppositeEntity: () => Models.CustomerAuditEntity,
		}),
		groupId: AttributeGroupOther,
		
	})
	public customerAuditss: Models.CustomerAuditEntity[] = [];

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Business Entity',
		displayType: 'reference-combobox',
		order: 590,
		referenceTypeFunc: () => Models.BusinessEntity,
		groupId: AttributeGroupOther,
		
	})
	public businessEntityId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public businessEntity: Models.BusinessEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Groups',
		displayType: 'reference-multicombobox',
		order: 600,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.GroupsCustomers,
		optionEqualFunc: makeJoinEqualsFunc('groupsId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'customerEntity',
			oppositeEntityName: 'groupEntity',
			relationName: 'customers',
			relationOppositeName: 'groups',
			entity: () => Models.CustomerEntity,
			joinEntity: () => Models.GroupsCustomers,
			oppositeEntity: () => Models.GroupEntity,
		}),
		groupId: AttributeGroupOther,
		
	})
	public groupss: Models.GroupsCustomers[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Monitoreds',
		displayType: 'reference-multicombobox',
		order: 610,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.MonitoredsCustomers,
		optionEqualFunc: makeJoinEqualsFunc('monitoredsId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'customerEntity',
			oppositeEntityName: 'monitoredEntity',
			relationName: 'customers',
			relationOppositeName: 'monitoreds',
			entity: () => Models.CustomerEntity,
			joinEntity: () => Models.MonitoredsCustomers,
			oppositeEntity: () => Models.MonitoredEntity,
		}),
		groupId: AttributeGroupOther,
		
	})
	public monitoredss: Models.MonitoredsCustomers[] = [];

	@observable
	@attribute()
	@CRUD({
		
		name: 'Organisation',
		displayType: 'reference-combobox',
		order: 620,
		referenceTypeFunc: () => Models.OrganisationEntity,
		groupId: AttributeGroupOther,
		
	})
	public organisationId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public organisation: Models.OrganisationEntity;

	/**
	 * Physical Address
	 */
	@observable
	@attribute({ isReference: true, manyReference: false })
	@CRUD({
		
		name: 'Physical Address',
		displayType: 'reference-combobox',
		order: 630,
		referenceTypeFunc: () => Models.AddressEntity,
		optionEqualFunc: (model, option) => model.id === option,
		inputProps: {
			fetchReferenceEntity: true,
		},
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'physicalAddresss',
			oppositeEntity: () => Models.AddressEntity,
		}),
		groupId: AttributeGroupOther,
		
	})
	public physicalAddress?: Models.AddressEntity;

	/**
	 * Postal Address
	 */
	@observable
	@attribute({ isReference: true, manyReference: false })
	@CRUD({
		
		name: 'Postal Address',
		displayType: 'reference-combobox',
		order: 640,
		referenceTypeFunc: () => Models.AddressEntity,
		optionEqualFunc: (model, option) => model.id === option,
		inputProps: {
			fetchReferenceEntity: true,
		},
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'postalAddresss',
			oppositeEntity: () => Models.AddressEntity,
		}),
		groupId: AttributeGroupOther,
		
	})
	public postalAddress?: Models.AddressEntity;

	// DEVELOPER NOTE:
	// - Not within protected region so put these changes back upon model generation.
	// - Should not be required, otherwise can't create customer without a report request
	// @Validators.Length(1)
	// @Validators.Required()
	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Report Requests',
		displayType: 'reference-multicombobox',
		order: 650,
		referenceTypeFunc: () => Models.ReportRequestEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'reportRequests',
			oppositeEntity: () => Models.ReportRequestEntity,
		}),
		groupId: AttributeGroupOther,
		
	})
	public reportRequests: Models.ReportRequestEntity[] = [];


	@observable
	public businessEntityStatus: boolean | null = null;

	@observable
	public liquorLicenceFinishedValidating: boolean = false;

	// eslint-disable-next-line max-len
	@observable
	public liquorLicenceResponseDetails?: { data: Record<string, LiquorLicenceDetails>; responseCode: number; responseMessage: string };

	@observable
	public selectedLiquorLicenceDetails?: LiquorLicenceDetails;

	/**
	 * The payment risk rating for the customer given this set of ATB data
	 */
	@observable
	public currentAtbRecord: Models.AtbRecordEntity;

	@observable
	public newestAlares: Models.AlaresReportEntity;

	@observable
	@attribute<CustomerEntity, Date>()
	@CRUD({
		name: 'Monitoring Payg End',
		displayType: 'datetimepicker',
		order: 660,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseDate,
		groupId: AttributeGroupMonitoring,
	})
	public monitoringPaygEnd: Date | null;

	@observable
	@attribute<CustomerEntity, CustomerCreationSource>()
	@CRUD({
		name: 'Creation Source',
		displayType: 'enum-combobox',
		order: 540,
		headerColumn: false,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, customerCreationSourceOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(customerCreationSourceOptions),
		displayFunction: (attr: CustomerCreationSource) => customerCreationSourceOptions[attr],
		groupId: AttributeGroupOther,
	})
	public creationSource: CustomerCreationSource;

	public attributeGroups = [
		{
			id: AttributeGroupMonitoring,
			order: AttributeGroupMonitoring,
			name: 'Monitoring',
			showName: true,
		},
		{
			id: AttributeGroupOther,
			order: AttributeGroupOther,
			name: 'Other',
			showName: true,
		}
	] as IAttributeGroup[];



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<ICustomerEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<ICustomerEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.drinksLicence !== undefined) {
				this.drinksLicence = attributes.drinksLicence;
			}

			if (attributes.email !== undefined) {
				this.email = attributes.email;
			}


			if (attributes.tradingTerms !== undefined) {
				this.tradingTerms = attributes.tradingTerms;
			}
			if (attributes.purchasingOfficerEmail !== undefined) {
				this.purchasingOfficerEmail = attributes.purchasingOfficerEmail;
			}

			if (attributes.taxInvoicesEmail !== undefined) {
				this.taxInvoicesEmail = attributes.taxInvoicesEmail;
			}
			if (attributes.trusteeACN !== undefined) {
				this.trusteeACN = attributes.trusteeACN;
			}
			if (attributes.statementsEmail !== undefined) {
				this.statementsEmail = attributes.statementsEmail;
			}
			if (attributes.acn !== undefined) {
				this.acn = attributes.acn;
			}


			if (attributes.accountsPhoneNumber !== undefined) {
				this.accountsPhoneNumber = attributes.accountsPhoneNumber;
			}
			if (attributes.creditLimit !== undefined) {
				this.creditLimit = attributes.creditLimit;
			}


			if (attributes.abn !== undefined) {
				this.abn = attributes.abn;
			}

			if (attributes.businessName !== undefined) {
				this.businessName = attributes.businessName;
			}

			if (attributes.debtorID !== undefined) {
				this.debtorID = attributes.debtorID;
			}


			if (attributes.dateOfBirth !== undefined) {
				if (attributes.dateOfBirth === null) {
					this.dateOfBirth = attributes.dateOfBirth;
				} else {
					this.dateOfBirth = moment(attributes.dateOfBirth).toDate();
				}
			}

			if (attributes.firstName !== undefined) {
				this.firstName = attributes.firstName;
			}

			if (attributes.nameForInvoice !== undefined) {
				this.nameForInvoice = attributes.nameForInvoice;
			}

			if (attributes.phone !== undefined) {
				this.phone = attributes.phone;
			}
			if (attributes.taxInvoicesContact !== undefined) {
				this.taxInvoicesContact = attributes.taxInvoicesContact;
			}
			if (attributes.drinksLicenceLastUpdated !== undefined) {
				if (attributes.drinksLicenceLastUpdated === null) {
					this.drinksLicenceLastUpdated = attributes.drinksLicenceLastUpdated;
				} else {
					this.drinksLicenceLastUpdated = moment(attributes.drinksLicenceLastUpdated).toDate();
				}
			}
			if (attributes.paymentRiskRating !== undefined) {
				this.paymentRiskRating = attributes.paymentRiskRating;
			}

			if (attributes.paymentRiskRatingLastUpdated !== undefined) {
				if (attributes.paymentRiskRatingLastUpdated === null) {
					this.paymentRiskRatingLastUpdated = attributes.paymentRiskRatingLastUpdated;
				} else {
					this.paymentRiskRatingLastUpdated = moment(attributes.paymentRiskRatingLastUpdated).toDate();
				}
			}

			if (attributes.purchasingOfficer !== undefined) {
				this.purchasingOfficer = attributes.purchasingOfficer;
			}
			if (attributes.statementsContact !== undefined) {
				this.statementsContact = attributes.statementsContact;
			}
			if (attributes.sicCode !== undefined) {
				this.sicCode = attributes.sicCode;
			}
			if (attributes.drinksLicenceInfo !== undefined) {
				this.drinksLicenceInfo = attributes.drinksLicenceInfo;
			}


			if (attributes.drinksLicenceState !== undefined) {
				this.drinksLicenceState = attributes.drinksLicenceState;
			}
			if (attributes.lastName !== undefined) {
				this.lastName = attributes.lastName;
			}
			if (attributes.monitoredStatus !== undefined) {
				this.monitoredStatus = attributes.monitoredStatus;
			}
			if (attributes.drinksLicenceStatus !== undefined) {
				this.drinksLicenceStatus = attributes.drinksLicenceStatus;
			}
			if (attributes.accountsContact !== undefined) {
				this.accountsContact = attributes.accountsContact;
			}
			if (attributes.atbRecords !== undefined && Array.isArray(attributes.atbRecords)) {
				for (const model of attributes.atbRecords) {
					if (model instanceof Models.AtbRecordEntity) {
						this.atbRecords.push(model);
					} else {
						this.atbRecords.push(new Models.AtbRecordEntity(model));
					}
				}
			}

			if (attributes.customerAuditss !== undefined && Array.isArray(attributes.customerAuditss)) {
				for (const model of attributes.customerAuditss) {
					if (model instanceof Models.CustomerAuditEntity) {
						this.customerAuditss.push(model);
					} else {
						this.customerAuditss.push(new Models.CustomerAuditEntity(model));
					}
				}
			}
			if (attributes.businessEntityId !== undefined) {
				this.businessEntityId = attributes.businessEntityId;
			}
			if (attributes.businessEntity !== undefined) {
				if (attributes.businessEntity === null) {
					this.businessEntity = attributes.businessEntity;
				} else if (attributes.businessEntity instanceof Models.BusinessEntity) {
					this.businessEntity = attributes.businessEntity;
					this.businessEntityId = attributes.businessEntity.id;
				} else {
					this.businessEntity = new Models.BusinessEntity(attributes.businessEntity);
					this.businessEntityId = this.businessEntity.id;
				}
			}
			if (attributes.groupss !== undefined && Array.isArray(attributes.groupss)) {
				for (const model of attributes.groupss) {
					if (model instanceof Models.GroupsCustomers) {
						this.groupss.push(model);
					} else {
						this.groupss.push(new Models.GroupsCustomers(model));
					}
				}
			}
			if (attributes.monitoredss !== undefined && Array.isArray(attributes.monitoredss)) {
				for (const model of attributes.monitoredss) {
					if (model instanceof Models.MonitoredsCustomers) {
						this.monitoredss.push(model);
					} else {
						this.monitoredss.push(new Models.MonitoredsCustomers(model));
					}
				}
			}
			if (attributes.organisationId !== undefined) {
				this.organisationId = attributes.organisationId;
			}
			if (attributes.organisation !== undefined) {
				if (attributes.organisation === null) {
					this.organisation = attributes.organisation;
				} else if (attributes.organisation instanceof Models.OrganisationEntity) {
					this.organisation = attributes.organisation;
					this.organisationId = attributes.organisation.id;
				} else {
					this.organisation = new Models.OrganisationEntity(attributes.organisation);
					this.organisationId = this.organisation.id;
				}
			}
			if (attributes.physicalAddress !== undefined) {
				if (attributes.physicalAddress === null) {
					this.physicalAddress = attributes.physicalAddress;
				} else if (attributes.physicalAddress instanceof Models.AddressEntity) {
					this.physicalAddress = attributes.physicalAddress;
				} else {
					this.physicalAddress = new Models.AddressEntity(attributes.physicalAddress);
				}
			}
			if (attributes.postalAddress !== undefined) {
				if (attributes.postalAddress === null) {
					this.postalAddress = attributes.postalAddress;
				} else if (attributes.postalAddress instanceof Models.AddressEntity) {
					this.postalAddress = attributes.postalAddress;
				} else {
					this.postalAddress = new Models.AddressEntity(attributes.postalAddress);
				}
			}
			if (attributes.reportRequests !== undefined && Array.isArray(attributes.reportRequests)) {
				for (const model of attributes.reportRequests) {
					if (model instanceof Models.ReportRequestEntity) {
						this.reportRequests.push(model);
					} else {
						this.reportRequests.push(new Models.ReportRequestEntity(model));
					}
				}
			}

			if (attributes?.currentAtbRecord !== undefined) {
				if (attributes.currentAtbRecord === null) {
					this.currentAtbRecord = attributes.currentAtbRecord;
				} else if (attributes.currentAtbRecord instanceof Models.AtbRecordEntity) {
					this.currentAtbRecord = attributes.currentAtbRecord;
				} else {
					this.currentAtbRecord = new Models.AtbRecordEntity(attributes.currentAtbRecord);
				}
			}
			if (attributes.newestAlares !== undefined) {
				if (attributes.newestAlares === null) {
					this.newestAlares = attributes.newestAlares;
				} else if (attributes.newestAlares instanceof Models.AlaresReportEntity) {
					this.newestAlares = attributes.newestAlares;
				} else {
					this.newestAlares = new Models.AlaresReportEntity(attributes.newestAlares);
				}
			}
			if (attributes?.businessEntityStatus !== undefined) {
				this.businessEntityStatus = attributes.businessEntityStatus;
			}
			if (attributes?.liquorLicenceFinishedValidating !== undefined) {
				this.liquorLicenceFinishedValidating = attributes.liquorLicenceFinishedValidating;
			}

			if (attributes?.liquorLicenceResponseDetails !== undefined) {
				this.liquorLicenceResponseDetails = attributes.liquorLicenceResponseDetails;
			}

			if (attributes?.selectedLiquorLicenceDetails !== undefined) {
				this.selectedLiquorLicenceDetails = attributes.selectedLiquorLicenceDetails;
			}

			if (attributes.monitoringPaygEnd != null) {
				this.monitoringPaygEnd = moment(attributes.monitoringPaygEnd).utc().toDate();
			}

			if (attributes.creationSource !== undefined) {
				this.creationSource = attributes.creationSource;
			}
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		groupss {
			${Models.GroupsCustomers.getAttributes().join('\n')}
			groups {
				${Models.GroupEntity.getAttributes().join('\n')}
			}
		}
		monitoredss {
			${Models.MonitoredsCustomers.getAttributes().join('\n')}
			monitoreds {
				${Models.MonitoredEntity.getAttributes().join('\n')}
			}
		}
		atbRecords {
			${Models.AtbRecordEntity.getAttributes().join('\n')}
		}
		customerAuditss {
			${Models.CustomerAuditEntity.getAttributes().join('\n')}
		}
		businessEntity {
			${Models.BusinessEntity.getAttributes().join('\n')}
		}
		organisation {
			${Models.OrganisationEntity.getAttributes().join('\n')}
			${Models.OrganisationEntity.getFiles().map(f => f.name).join('\n')}
		}
		physicalAddress {
			${Models.AddressEntity.getAttributes().join('\n')}
		}
		postalAddress {
			${Models.AddressEntity.getAttributes().join('\n')}
		}
		reportRequests {
			${Models.ReportRequestEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			groupss: {},
			monitoredss: {},
			atbRecords: {},
			customerAuditss: {},
			physicalAddress: {},
			postalAddress: {},
			reportRequests: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'atbRecords',
							'groupss',
							'monitoredss',
							'physicalAddress',
							'postalAddress',
							'reportRequests',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		return this.businessName ?? this.id;
		// % protected region % [Customise the display name for this entity] end
	}

	@action
	clearLiquorLicenceFields() {
		this.drinksLicenceLastUpdated = new Date(1001, 0, 1);
		this.drinksLicenceStatus = '';
		this.drinksLicenceInfo = '';
		this.liquorLicenceResponseDetails = undefined;
		this.selectedLiquorLicenceDetails = undefined;
	}

	@action
	updateLiquorLicenceFields(uniqueId: string) {
		const responseCode = this.liquorLicenceResponseDetails?.responseCode;
		const responseMessage = this.liquorLicenceResponseDetails?.responseMessage;

		const availableLiquorLicenceDetails = this.liquorLicenceResponseDetails?.data;
		if (availableLiquorLicenceDetails !== undefined) {
			const liquorLicenceDetailsArray = Object.values(availableLiquorLicenceDetails);
			const selectedLiquorLicenceDetails: LiquorLicenceDetails | undefined = liquorLicenceDetailsArray.find(
				liquorLicenceDetails => liquorLicenceDetails.uniqueId === uniqueId,
			);
			if (selectedLiquorLicenceDetails !== undefined) {
				this.selectedLiquorLicenceDetails = selectedLiquorLicenceDetails;
				this.drinksLicence = selectedLiquorLicenceDetails.licenceNumber;
				this.drinksLicenceStatus = selectedLiquorLicenceDetails.status;
				// eslint-disable-next-line max-len
				this.drinksLicenceLastUpdated = new Date(selectedLiquorLicenceDetails.dateModified ?? new Date(1001, 0, 1));
				// eslint-disable-next-line max-len
				const state = Enums.stateOptions[selectedLiquorLicenceDetails.serialState ?? this.drinksLicenceState] as Enums.state;
				this.drinksLicenceState = state;
			}

			const updatedLicenceDetails: Record<string, LiquorLicenceDetails> = {};
			for (const liquorLicenceDetails of liquorLicenceDetailsArray) {
				const isSelected = liquorLicenceDetails.uniqueId === uniqueId;
				// eslint-disable-next-line max-len
				updatedLicenceDetails[`${liquorLicenceDetails.uniqueId.toUpperCase()}::${isSelected ? 'TRUE' : 'FALSE'}`] = liquorLicenceDetails;
			}
			this.drinksLicenceInfo = JSON.stringify(updatedLicenceDetails);
			this.liquorLicenceResponseDetails = {
				data: updatedLicenceDetails,
				responseCode: responseCode ?? 0,
				responseMessage: responseMessage ?? '',
			};

			this.setFieldError('drinksLicence', false, '');
			this.setFieldError('drinksLicenceState', false, '');
		}
	}

	@action
	async runLiquorLicenceValidation() {
		this.liquorLicenceFinishedValidating = true;
		this.drinksLicence = this.drinksLicence ? this.drinksLicence.replace(/\W/g, '') : this.drinksLicence;

		if (this.drinksLicenceState === 'NONE') {
			this.liquorLicenceFinishedValidating = false;
			this.setFieldError('drinksLicenceState', false, '');
			if (this.drinksLicence) {
				this.setFieldError('drinksLicence', true, 'Licence state must be selected');
			} else {
				this.setFieldError('drinksLicence', false, '');
			}
			this.clearLiquorLicenceFields();
			return true;
		}

		if (!this.drinksLicence) {
			this.liquorLicenceFinishedValidating = false;
			this.setFieldError('drinksLicence', true, 'Licence number cannot be empty');
			this.clearLiquorLicenceFields();
			return false;
		}

		if (this.drinksLicence.length < 5) {
			this.liquorLicenceFinishedValidating = false;
			this.setFieldError('drinksLicence', true, 'Licence number must be at least 5 characters');
			this.clearLiquorLicenceFields();
			return false;
		}

		if (!LocationUtil.isTypeOfState(this.drinksLicenceState)) {
			this.setFieldError('drinksLicence', false, '');
			this.setFieldError('drinksLicenceState', true, 'Licence issued state must be a valid state');
			this.clearLiquorLicenceFields();
			return false;
		}

		const response = await validateLiquorLicenceNumber(this.drinksLicence, this.drinksLicenceState, this.id);

		if (!response) {
			runInAction(() => {
				this.liquorLicenceFinishedValidating = true;
				this.clearLiquorLicenceFields();
				this.setFieldError('drinksLicence', true, 'Licence number not found');
				this.setFieldError('drinksLicenceState', true, 'Licence state not matched with the licence number.');
			});
			return false;
		}

		if (response.exception && !response.data) {
			runInAction(() => {
				this.liquorLicenceFinishedValidating = true;
				this.clearLiquorLicenceFields();
				this.setFieldError('drinksLicence', true, response.exception.exceptionDescription);
				this.setFieldError('drinksLicenceState', true, 'Licence state not matched with the licence number.');
			});
			return false;
		}

		// Record keys format is uniqueId::isSelected
		const parsedLicenceDetails = JSON.parse(response.data.licenceDetails) as Record<string, LiquorLicenceDetails>;

		if (!parsedLicenceDetails || Object.keys(parsedLicenceDetails).length < 1) {
			runInAction(() => {
				this.liquorLicenceFinishedValidating = true;
				this.clearLiquorLicenceFields();
				this.liquorLicenceResponseDetails = undefined;
				this.setFieldError('drinksLicence', true, 'Licence number not found');
				this.setFieldError('drinksLicenceState', true, 'Licence state not matched with the licence number.');
			});

			return false;
		}

		let selectedLiquorLicenceDetails: LiquorLicenceDetails | undefined;

		for (const [key, value] of Object.entries(parsedLicenceDetails)) {
			const isSelected = key.toUpperCase().split('::')[1] === 'TRUE';
			const uniqueId = key.toUpperCase().split('::')[0];

			if (isSelected && uniqueId === value.uniqueId) {
				selectedLiquorLicenceDetails = value;
			}
		}

		// if there is only one licence detail found, we can just set the liquor
		if (selectedLiquorLicenceDetails !== undefined && Object.keys(parsedLicenceDetails).length > 0) {
			runInAction(() => {
				this.liquorLicenceFinishedValidating = true;
				this.drinksLicence = selectedLiquorLicenceDetails?.licenceNumber ?? '-';
				// eslint-disable-next-line max-len
				this.drinksLicenceLastUpdated = new Date(selectedLiquorLicenceDetails?.dateModified ?? new Date(1001, 0, 1));
				this.drinksLicenceStatus = selectedLiquorLicenceDetails?.status ?? '-';
				this.drinksLicenceInfo = response.data.licenceDetails;
				this.liquorLicenceResponseDetails = {
					data: parsedLicenceDetails,
					responseCode: response.data.responseCode,
					responseMessage: response.data.responseMessage,
				};
				this.selectedLiquorLicenceDetails = selectedLiquorLicenceDetails;
				this.setFieldError('drinksLicence', false, '');
				this.setFieldError('drinksLicenceState', false, '');
			});

			return true;
		}

		// if there are multiple licence details found, we need to show the user a modal
		runInAction(() => {
			this.liquorLicenceFinishedValidating = true;
			this.clearLiquorLicenceFields();
			this.liquorLicenceResponseDetails = {
				data: parsedLicenceDetails,
				responseCode: response.data.responseCode,
				responseMessage: response.data.responseMessage,
			};
			this.setFieldError('drinksLicence', true, 'Multiple Licence Details Found Choose one');
			this.setFieldError('drinksLicenceState', false, '');
		});

		return false;
	}


	@action public validateField = async (field: string) => {
		switch (field) {
			// Only do length validation on the business number as the BusinessValidationModel handles the api validation
			case 'abn': {
				const abnErrors = validateAbn(this.abn, false);
				this.setFieldError('abn', abnErrors.length > 0, abnErrors[0] || 'Invalid ABN');
				break;
			}
			case 'acn': {
				const acnErrors = validateAcn(this.acn);
				this.setFieldError('acn', !!this.acn && acnErrors.length > 0, acnErrors[0] || 'Invalid ACN');
				break;
			}
			case 'trusteeACN': {
				// Dev note - don't need to do format here - we do custom one outside
				this.setFieldError('trusteeACN', !!this.trusteeACN && this.trusteeACN === this.acn, 'Trustee ACN and ACN can not match');
				break;
			}

			case 'accountsPhoneNumber': {
				this.setFieldError('accountsPhoneNumber', !!this.accountsPhoneNumber
					&& !isPhone(this.accountsPhoneNumber), 'Accounts contact number must be a valid phone number');
				break;
			}
			case 'taxInvoicesEmail': {
				this.setFieldError('taxInvoicesEmail', !!this.taxInvoicesEmail
					&& !isEmail(this.taxInvoicesEmail), 'Tax invoice email must be a valid email address');
				break;
			}
			case 'statementsEmail': {
				this.setFieldError(
					'statementsEmail',
					!!this.statementsEmail && !isEmail(this.statementsEmail),
					'Statements email must be a valid email address',
				);
				break;
			}
			case 'purchasingOfficerEmail': {
				this.setFieldError('purchasingOfficerEmail', !!this.purchasingOfficerEmail
					&& !isEmail(this.purchasingOfficerEmail), 'Purchasing officer email must be a valid email address');
				break;
			}
			case 'physicalAddress.postcode': {
				// Borrow errors from the address entity
				this.physicalAddress?.validateField('postcode');
				break;
			}
			case 'postalAddress.postcode': {
				// Borrow errors from the address entity
				this.postalAddress?.validateField('postcode');
				break;
			}
			case 'businessName': {
				this.setFieldError('businessName', this.businessName == null || this.businessName == '', 'Business name must be set');
				break;
			}
			case 'businessEntity': {
				this.setFieldError('businessEntity', !this.businessEntityId, 'Business Entity must be selected');
				break;
			}
			case 'drinksLicence': {
				await this.runLiquorLicenceValidation();
				break;
			}
			case 'debtorID': {
				if (!!this.debtorID) {
					if (!this.businessEntityId) {
						this.setFieldError('businessEntity', true, 'Business Entity must be selected');
						break;
					}
					const { data } = await getCustomers({
						filterByBusinessIds: [this.businessEntityId],
						searchBy: this.debtorID,
						searchOn: ['debtorID'],
					});
					const isDuplicate = data.data.some(customer => customer.debtorID === this.debtorID && customer.id !== this.id);
					this.setFieldError('debtorID', isDuplicate, 'Debtor ID must be unique per customer');
				}
				break;
			}
		}
	};

	public validateAddCustomerFields = async () => {
		const fieldList = [
			'abn', 'acn', 'trusteeACN', 'businessName', 'businessEntity',
		];
		const validationPromises = fieldList.map(field => this.validateField(field));
		await Promise.all(validationPromises);
	};
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(CustomerEntity.prototype, 'created');
CRUD(modifiedAttr)(CustomerEntity.prototype, 'modified');
